import {
    type ChatMessage,
    getAssistantDisplayContent,
} from '@fruitful/llm-service-shared';
import { AnalyticsUserEvent } from '@fruitful/types';
import { useCallback, useEffect, useRef } from 'react';

import DatadogFacade from '../../logger/datadog/DatadogFacade';
import { track } from '../../logger/Logger';
import { getWidgetTypeTrackingInfo } from '../utils/getWidgetTypeTrackingInfo';

/** RUM action for member-perceived wait until first visible assistant content. */
export const AI_CHAT_CLIENT_TIME_TO_FIRST_TOKEN_ACTION =
    'AIChat_ClientTimeToFirstToken';

export interface AssistantContentPartRenderedEvent {
    messageId: string;
    messagePartIndex: number;
    messageType: 'text' | 'ui';
    messageLength?: number;
    messageWidgetType?: string;
    messageWidgetProps?: Record<string, unknown> | null;
}

const getUserMessageRenderKey = (messageId: string): string =>
    `user-${messageId}`;

const getAgentMessagePartRenderKey = (
    messageId: string,
    messagePartIndex: number,
): string => `assistant-${messageId}-${messagePartIndex.toString()}`;

const userMessageHasText = (message: ChatMessage): boolean =>
    message.content.some(
        (content) => content.type === 'text' && content.text.trim() !== '',
    );

const seedRestoredMessageRenderKeys = (
    messages: ChatMessage[],
    processedMessageRenderKeys: Set<string>,
): void => {
    for (const message of messages) {
        if (message.role === 'user') {
            processedMessageRenderKeys.add(getUserMessageRenderKey(message.id));
        } else if (message.role === 'assistant') {
            const contentParts = getAssistantDisplayContent(message);
            for (let index = 0; index < contentParts.length; index += 1) {
                processedMessageRenderKeys.add(
                    getAgentMessagePartRenderKey(message.id, index),
                );
            }
        }
    }
};

export const useAiChatRenderAnalytics = (
    messages: ChatMessage[],
    isRestoringHistory: boolean,
) => {
    const lastAgentResponseRenderedAtRef = useRef<number | null>(null);
    const lastUserResponseRenderedAtRef = useRef<number | null>(null);
    const processedMessageRenderKeysRef = useRef(new Set<string>());
    const wasRestoringHistoryRef = useRef(isRestoringHistory);

    const trackUserResponseRendered = useCallback(
        (messageId: string) => {
            const renderKey = getUserMessageRenderKey(messageId);
            if (processedMessageRenderKeysRef.current.has(renderKey)) {
                return;
            }
            processedMessageRenderKeysRef.current.add(renderKey);

            const now = Date.now();
            const elapsedMsSinceLastAgentMessage =
                lastAgentResponseRenderedAtRef.current != null
                    ? now - lastAgentResponseRenderedAtRef.current
                    : undefined;

            track(AnalyticsUserEvent.AI_CHAT_USER_RESPONSE_RENDERED, {
                messageId,
                messageCount: messages.length,
                ...(elapsedMsSinceLastAgentMessage != null
                    ? { elapsedMsSinceLastAgentMessage }
                    : {}),
            });
            lastUserResponseRenderedAtRef.current = now;
        },
        [messages.length],
    );

    const trackAgentResponsePartRendered = useCallback(
        (event: AssistantContentPartRenderedEvent) => {
            const {
                messageId,
                messagePartIndex,
                messageType,
                messageLength = 0,
                messageWidgetType = '',
                messageWidgetProps,
            } = event;
            const renderKey = getAgentMessagePartRenderKey(
                messageId,
                messagePartIndex,
            );
            if (processedMessageRenderKeysRef.current.has(renderKey)) {
                return;
            }

            // can't just check messagePartIndex = 0 because that index could have been null or not text/ui
            // and must check before updating processedMessageRenderKeysRef
            const isFirstPart = ![
                ...processedMessageRenderKeysRef.current,
            ].some((key) => key.startsWith(`assistant-${messageId}-`));
            processedMessageRenderKeysRef.current.add(renderKey);

            const now = Date.now();
            const elapsedMsSinceLastUserMessage =
                isFirstPart && lastUserResponseRenderedAtRef.current != null
                    ? now - lastUserResponseRenderedAtRef.current
                    : undefined;
            const eventType =
                messageType === 'text'
                    ? AnalyticsUserEvent.AI_CHAT_AGENT_RESPONSE_TEXT_RENDERED
                    : AnalyticsUserEvent.AI_CHAT_AGENT_RESPONSE_WIDGET_RENDERED;
            const messageCount = messages.length;

            track(eventType, {
                messageId,
                messagePartIndex,
                messageCount,
                ...(messageType === 'text'
                    ? { messageLength }
                    : { messageWidgetType }),
                ...(elapsedMsSinceLastUserMessage != null
                    ? { elapsedMsSinceLastUserMessage }
                    : {}),
            });

            if (elapsedMsSinceLastUserMessage != null) {
                DatadogFacade.addAction(
                    AI_CHAT_CLIENT_TIME_TO_FIRST_TOKEN_ACTION,
                    {
                        elapsedMsSinceLastUserMessage,
                        messageType,
                        messageId,
                    },
                );
            }

            if (messageType === 'ui' && messageWidgetType !== '') {
                const trackingInfo = getWidgetTypeTrackingInfo(
                    messageWidgetType,
                    messageWidgetProps,
                );
                if (trackingInfo?.eventType != null) {
                    track(trackingInfo.eventType, trackingInfo.eventProps);
                }
            }

            lastAgentResponseRenderedAtRef.current = now;
        },
        [messages.length],
    );

    // Helps prevent logging events for every message when restoring a chat from history
    useEffect(() => {
        const wasRestoringHistory = wasRestoringHistoryRef.current;
        wasRestoringHistoryRef.current = isRestoringHistory;

        if (wasRestoringHistory && !isRestoringHistory && messages.length > 0) {
            seedRestoredMessageRenderKeys(
                messages,
                processedMessageRenderKeysRef.current,
            );
        }
    }, [isRestoringHistory, messages]);

    useEffect(() => {
        if (isRestoringHistory) {
            return undefined;
        }

        for (const message of messages) {
            if (message.role !== 'user') {
                continue;
            }

            const renderKey = getUserMessageRenderKey(message.id);
            if (processedMessageRenderKeysRef.current.has(renderKey)) {
                continue;
            }

            if (!userMessageHasText(message)) {
                processedMessageRenderKeysRef.current.add(renderKey);
                continue;
            }

            trackUserResponseRendered(message.id);
        }
    }, [isRestoringHistory, messages, trackUserResponseRendered]);

    return {
        trackAgentResponsePartRendered,
    };
};
